import { DotLegalDialog, DotLegalHeader, DotLegalLink } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { Box } from "@mui/material";
import { QuestionnaireSelectableItem } from "../../legalEntityAuditTab/LegalEntityAudit.types";
import { apiUrls } from "../../../common/api/apiUrls";
import DotLegalMultiSelectApi from "../../../common/components/dotLegalMultiSelectApi/DotLegalMultiSelectApi";
import useLegalEntityClassificationDialogHooks from "./LegalEntityClassificationDialog.hooks";
import React from "react";
import DotLegalSelectApi from "../../../common/components/dotLegalSelectApi/DotLegalSelectApi";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";
import { PlanLimit } from "../../../auth/planProvider/PlanProvider.types.ts";
import TrialUsageWarning from "../../../common/components/TrialUsageWarning.tsx";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";

export interface ILegalEntityClassificationDialog {
    legalEntityId: string;
    onDialogClose: () => void;
    onCreated: (responseId?: string) => void;
    planLimit: PlanLimit | undefined;
    processingActivityId?: string;
    btnText?: string;
    infoText?: string;
}

function LegalEntityClassificationDialog(props: ILegalEntityClassificationDialog) {
    const urlProvider = useUrlProvider();

    const { permissions } = useUserContext();
    const { vendorManagementFeatures } = usePlanContext();
    const { translateString } = useTranslation();
    const { onQuestionnaireChanged, onQuestionnairesChanged, saveModel, validation, onSaveClick, onSystemChange, onProcessingActivityChange, isSaving } =
        useLegalEntityClassificationDialogHooks(props);

    validation.check(saveModel);

    return (
        <DotLegalDialog
            size={"sm"}
            header={translateString("addClassification")}
            buttonOkText={props.btnText ? props.btnText : translateString("save")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={() => onSaveClick()}
            inProgress={isSaving}
            disableBackdropOnCloseIfValueChange={saveModel}
        >
            <Box ml={2} mr={2} px={"10px"}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <DotLegalHeader center fontWeight={400} headerStyle={"small"}>
                        {props.infoText ? props.infoText : translateString("addClassificationInfo")}
                    </DotLegalHeader>
                </Box>

                {!props.processingActivityId ? (
                    <DotLegalMultiSelectApi<QuestionnaireSelectableItem>
                        url={apiUrls().vendorClassifications.selectableItems}
                        onChange={onQuestionnairesChanged}
                        label={translateString("classificationTemplate")}
                        noOptionsLabel={translateString("noOptions")}
                        chosenOptions={saveModel?.questionnaireIds ?? []}
                        errorText={validation.getErrorsForDisplay("questionnaireIds")}
                        toolTipText={translateString("selectClassificationInfo")}
                    />
                ) : (
                    <DotLegalSelectApi
                        url={apiUrls().vendorClassifications.selectableItems}
                        onChange={onQuestionnaireChanged}
                        label={translateString("classificationTemplate")}
                        noOptionsLabel={translateString("noOptions")}
                        toolTipText={translateString("selectClassificationInfo")}
                        errorText={validation.getErrorsForDisplay("questionnaireIds")}
                        selectedItem={saveModel?.questionnaireIds.length > 0 ? saveModel?.questionnaireIds[0] : undefined}
                    />
                )}

                {!props.processingActivityId && vendorManagementFeatures.hasVendorManagement && (
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} fontWeight={400}>
                            <DotLegalLink linkColor={"primary"} to={urlProvider.getVendorClassifications()}>
                                {translateString("createAuditTemplate")}
                            </DotLegalLink>
                        </DotLegalHeader>
                    </Box>
                )}

                {!props.processingActivityId && permissions.systemPermissions.read && (
                    <DotLegalSelectApi
                        url={apiUrls().systems.selectableItemsByLegalEntityId(props.legalEntityId)}
                        onChange={onSystemChange}
                        label={translateString("assetOptional")}
                        noOptionsLabel={translateString("noOptions")}
                        selectedItem={saveModel?.systemId}
                        disabled={saveModel?.processingActivityId !== undefined}
                    />
                )}

                {permissions.processingActivityPermissions.read && (
                    <DotLegalSelectApi
                        url={apiUrls().processingActivites.selectableItemsByAccess}
                        onChange={onProcessingActivityChange}
                        label={translateString("processingActivityOptional")}
                        noOptionsLabel={translateString("noOptions")}
                        selectedItem={saveModel?.processingActivityId}
                        disabled={saveModel?.systemId !== undefined || props.processingActivityId !== undefined}
                    />
                )}

                {props.planLimit?.warnLimitReachedOnTrial && (
                    <TrialUsageWarning textKey={translateString("createExceedingNumberOfClassificationsWarning")} marginTop={2} />
                )}
            </Box>
        </DotLegalDialog>
    );
}

export default LegalEntityClassificationDialog;
